/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  Navigation constants
 */

import type { MenuNavLink } from '../utils/getMenuNavLinks'

export enum NAV_SELECTIONS {
  LONG_TERM = 'Long-term',
  SHORT_TERM = 'Short-term',
}

export const TRACK_NAVBAR_ACTION = {
  LOGO: 'logo',
  SEARCH: 'search',
  ALERTS: 'alerts',
  FAVORITED: 'favorites',
  MESSAGED: 'messaged',
  POST_YOUR_RENTAL: 'post a listing',
  LOG_IN: 'login',
  LOG_OUT: 'logged out',
  SIGN_UP: 'sign up',
  APPLICATIONS: 'applications',
  MANAGE_LISTINGS: 'manage your listings',
  ADVERTISE: 'advertise',
  PROFILE: 'profile',
  FOR_YOU: 'for you',
  RENTALS: 'rentals',
  APARTMENTS_FOR_RENT: 'apartments for rent',
  HOUSES_FOR_RENT: 'houses for rent',
  ROOMS_FOR_RENT: 'rooms for rent',
  AMENITIES: 'amenities',
  AFR_CHEAP: 'cheap apartments for rent',
  AFR_LUXURY: 'luxury apartments for rent',
  AFR_PET_FRIENDLY: 'pet-friendly apartments for rent',
  AFR_SHORT_TERM: 'short term apartments for rent',
  HOME: 'home',
} as const

export const TRACK_USER_MENU_ACTION = {
  MENU_OPEN: 'Menu Open',

  // Nearby Rentals Section
  APARTMENTS_FOR_RENT_NEAR_ME: 'Apartments for Rent Near Me',
  HOUSES_FOR_RENT_NEAR_ME: 'Houses for Rent Near Me',
  ROOMS_FOR_RENT_NEAR_ME: 'Rooms for Rent Near Me',

  // Amenities submenu
  AFR_CHEAP_NEAR_ME: 'Cheap Apartments for Rent Near Me',
  AFR_LUXURY_NEAR_ME: 'Luxury Apartments for Rent Near Me',
  AFR_PET_FRIENDLY_NEAR_ME: 'Pet-Friendly Apartments for Rent Near Me',
  AFR_SHORT_TERM_NEAR_ME: 'Short Term Apartments for Rent Near Me',

  // Renter Hub section
  FAVORITES: 'Favorites',
  APPLICATIONS: 'Applications',
  RENT_RESEARCH: 'Rent Research',
  RENT_CALCULATOR: 'Rent Calculator',
  BLOG: 'Blog',
  PROFILE: 'Profile',
  SAVED_SEARCHES: 'Saved Searches',

  // Property Management section
  POST_LISTING: 'Post a Listing',
  ADVERTISE: 'Advertise',
  MANAGE_LISTINGS: 'Manage Listings',
  LOG_IN: 'Login',
  LOG_OUT: 'Logged Out',
  SIGN_UP: 'Sign Up',
} as const

export const ANALYTICS_EVENT = {
  RENTALS: 'Rentals',
  APARTMENTS_FOR_RENT: 'Apartments For Rent',
  HOUSES_FOR_RENT: 'Houses For Rent',
  ROOMS_FOR_RENT: 'Rooms For Rent',
  AMENITIES: 'Amenities',
} as const

export const NAV_MODAL_TYPES = {
  DATES: 'dates',
  GUESTS: 'guests',
  PRICE: 'price',
  ROOMS: 'rooms',
} as const

// DATE PICKER CONSTANTS
export const DATE_PICKER_MODAL_ID = 'date-picker-modal'
export const SINGLE_CALENDAR_HEIGHT = 272
export const HEADER_OFFSET = 200

// optimizely ST onboarding constants
export const STR_ONBOARDING_SRP_VIEW = 'STR_ONBOARDING_SRP_VIEW'
export const SHOW_GLOBAL_NAV = 'SHOW_GLOBAL_NAV'

export const STR_ONBOARDING_SEARCH_RESULT_MWEB = 'STR_ONBOARDING_SEARCH_RESULT_MWEB'
export const GLOBAL_NAV = 'GLOBAL_NAV'

type RentalResourceLinksTrackingNames =
  | (typeof TRACK_USER_MENU_ACTION)['RENT_RESEARCH']
  | (typeof TRACK_USER_MENU_ACTION)['RENT_CALCULATOR']
  | (typeof TRACK_USER_MENU_ACTION)['BLOG']

type RentalResourceLinks = ReadonlyArray<
  { readonly trackingName: RentalResourceLinksTrackingNames } & Omit<MenuNavLink, 'is_indexed'>
>

export const RENTAL_RESOURCE_LINKS = [
  {
    name: 'Rent calculator',
    href: '/rent-calculator',
    trackingName: TRACK_USER_MENU_ACTION.RENT_CALCULATOR,
  },
  {
    name: 'Rent research',
    href: '/rent-research',
    trackingName: TRACK_USER_MENU_ACTION.RENT_RESEARCH,
  },
  {
    name: 'Renting tips & advice',
    href: '/blog',
    trackingName: TRACK_USER_MENU_ACTION.BLOG,
  },
] as const satisfies RentalResourceLinks
